import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import {RootState} from "../../../../../../app/store";
import {BotIntegrations, UpdateIntegrationRequest} from "../../../../../../api/generated";
import {integrationApi, RequestStatus} from "../../../../../../api/apiConfiguration";
import {selectExceptionally} from "../../../../../../utils/selectExceptionally";
import {prepareEmails} from "../../../botCreateSlice";

export type UpdateIntegrationRequestPartial = Partial<
    Omit<UpdateIntegrationRequest, "superchatHandoverUserEmailList"> & { superchatHandoverUserEmailList: string }
>

interface UpdateIntegrationState {
    updateIntegrationRequest: UpdateIntegrationRequestPartial
    updateIntegrationRequestStatus: RequestStatus
}

const buildInitialState = () => {
    return {
        updateIntegrationRequest: {} as UpdateIntegrationRequestPartial,
        updateIntegrationRequestStatus: RequestStatus.NONE
    }
}

const initialState: UpdateIntegrationState = buildInitialState()

export const updateIntegration = createAsyncThunk<BotIntegrations, undefined, { state: RootState }>(
    'updateIntegration/updateIntegration',
    (_payload, {getState}) => {
        const request = getState().updateIntegration.updateIntegrationRequest
        return integrationApi.updateIntegration({updateIntegrationRequest: prepareUpdateIntegrationRequest(request)})
    }
)

const prepareUpdateIntegrationRequest = (request: UpdateIntegrationRequestPartial): UpdateIntegrationRequest => {
    return {
        configurationId: selectExceptionally(request.configurationId),
        publicBotId: selectExceptionally(request.publicBotId),
        integration: selectExceptionally(request.integration),
        enabled: selectExceptionally(request.enabled),
        superchatApiKey: request.superchatApiKey,
        superchatWebhookId: request.superchatWebhookId,
        superchatAimeUserEmail: request.superchatAimeUserEmail,
        superchatHandoverUserEmailList: prepareEmails(request.superchatHandoverUserEmailList),
    } as UpdateIntegrationRequest
}

export const updateIntegrationSlice = createSlice({
    name: 'updateIntegration',
    initialState,
    reducers: {
        clearUpdateIntegrationState: () => {
            return buildInitialState();
        },
        updateUpdateIntegrationRequest: (state, {payload}: PayloadAction<UpdateIntegrationRequestPartial>) => {
            state.updateIntegrationRequest = {
                ...state.updateIntegrationRequest,
                ...payload
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(updateIntegration.fulfilled, (state) => {
            state.updateIntegrationRequestStatus = RequestStatus.RESOLVED
        });
        builder.addCase(updateIntegration.pending, (state) => {
            state.updateIntegrationRequestStatus = RequestStatus.PENDING
        });
        builder.addCase(updateIntegration.rejected, (state) => {
            state.updateIntegrationRequestStatus = RequestStatus.REJECTED
        });
    }
})

export const {
    clearUpdateIntegrationState,
    updateUpdateIntegrationRequest
} = updateIntegrationSlice.actions;

export const updateIntegrationRequestSelector = (state: RootState): UpdateIntegrationRequestPartial => state.updateIntegration.updateIntegrationRequest;
export const updateIntegrationRequestStatusSelector = (state: RootState): RequestStatus => state.updateIntegration.updateIntegrationRequestStatus;
